import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import apiClient from '../../../api/axios';

interface Group {
  id: string;
  name: string;
  description: string;
  groupType: string;
  isPrivate: boolean;
  isActive: boolean;
  memberCount: number;
  maxMembers?: number;
  color?: string;
  icon?: string;
  createdAt: string;
  updatedAt: string;
}

interface EditGroupModalProps {
  group: Group;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditGroupModal: React.FC<EditGroupModalProps> = ({
  group,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: group.name,
    description: group.description,
    groupType: group.groupType,
    isPrivate: group.isPrivate,
    excludeFromBillingRate: false,
    maxMembers: group.maxMembers,
    color: group.color || '#3B82F6',
    icon: group.icon || '',
    isActive: group.isActive
  });

  const queryClient = useQueryClient();

  // Update form data when group changes
  useEffect(() => {
    setFormData({
      name: group.name,
      description: group.description,
      groupType: group.groupType,
      isPrivate: group.isPrivate,
      excludeFromBillingRate: false,
      maxMembers: group.maxMembers,
      color: group.color || '#3B82F6',
      icon: group.icon || '',
      isActive: group.isActive
    });
  }, [group]);

  const updateGroupMutation = useMutation({
    mutationFn: async (updateData: any) => {
      const response = await apiClient.patch(`/admin/groups/${group.id}`, updateData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      onSuccess();
    }
  });

  const deleteGroupMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete(`/admin/groups/${group.id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      onSuccess();
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateGroupMutation.mutate(formData);
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete the group "${group.name}"? This action cannot be undone.`)) {
      deleteGroupMutation.mutate();
    }
  };

  const groupTypeOptions = [
    { value: 'department', label: 'Department' },
    { value: 'practice_team', label: 'Practice Team' },
    { value: 'project_team', label: 'Project Team' },
    { value: 'administrative', label: 'Administrative' },
    { value: 'client_team', label: 'Client Team' },
    { value: 'custom', label: 'Custom' }
  ];

  const iconOptions = [
    { value: '', label: 'No Icon' },
    { value: '👥', label: 'Team' },
    { value: '🏢', label: 'Department' },
    { value: '📋', label: 'Project' },
    { value: '⚖️', label: 'Legal' },
    { value: '💰', label: 'Billing' },
    { value: '📞', label: 'Support' },
    { value: '🔧', label: 'Technical' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit Group: {group.name}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {group.icon && <span className="mr-2 text-lg">{group.icon}</span>}
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Members:</strong> {group.memberCount}
                  {group.maxMembers && ` / ${group.maxMembers}`}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Created:</strong> {new Date(group.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            <Badge variant={group.isActive ? 'success' : 'destructive'}>
              {group.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Group Name *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Group Type *
            </label>
            <select
              value={formData.groupType}
              onChange={(e) => setFormData(prev => ({ ...prev, groupType: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {groupTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Icon
            </label>
            <select
              value={formData.icon}
              onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {iconOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label} {option.value}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Color
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-md"
              />
              <Input
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="flex-1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Maximum Members (Optional)
            </label>
            <Input
              type="number"
              value={formData.maxMembers || ''}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                maxMembers: e.target.value ? parseInt(e.target.value) : undefined 
              }))}
              placeholder="Leave empty for unlimited"
              min="1"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isPrivate}
                onChange={(e) => setFormData(prev => ({ ...prev, isPrivate: e.target.checked }))}
                className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Private Group (only admins can see)
              </span>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Group Active
              </span>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteGroupMutation.isPending}
              className="w-full mb-3"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              {deleteGroupMutation.isPending ? 'Deleting...' : 'Delete Group'}
            </Button>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateGroupMutation.isPending}
            >
              {updateGroupMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>

        {(updateGroupMutation.isError || deleteGroupMutation.isError) && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md">
            <p className="text-red-600 dark:text-red-400 text-sm">
              {updateGroupMutation.isError && 'Failed to update group. Please try again.'}
              {deleteGroupMutation.isError && 'Failed to delete group. Please try again.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}; 